import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useEquipmentLevel } from './components/useEquipmentLevel';
import Header from './components/Header';
import './Dashboard.scss';
import { useDashboard } from './useDashboard';
import logo from '../../../assets/eemdl-logo.png';
import { EquipmentLevel } from './components/EquipmentLevel';
import { FacilityLevel } from './components/FacilityLevel';
import { ComparisonLevel } from './components/ComparisonLevel';
import {
  Tabs,
  Tab,
  Box,
  Typography,
  Grid,
  CircularProgress,
} from '@mui/material';
import { useLoadingStore, useOperatorsStore } from './store/loadingStore';
import { BenchmarkLevel } from './components/BenchmarkLevel';

const Dashboard: React.FC = React.memo(() => {
  const { filters, timeperiods, subfilters, handleFilterChange, activeFilter } =
    useDashboard();

  const filteredTimeperiods = timeperiods.filter((item) =>
    item.includes('All'),
  );

  const operators = useOperatorsStore(
    (state: { operators: any }) => state.operators,
  );

  const [selectedPeriod, setSelectedPeriod] = useState<string>(timeperiods[0]);
  const [selectedSubfilter, setSelectedSubfilter] = useState(subfilters[0]);
  const [activeTab, setActiveTab] = useState(1);
  const [showLoader, setShowLoader] = useState(false);
  const isLoading = useLoadingStore(
    (state: { isLoading: any }) => state.isLoading,
  );

  const handlePeriodChange = useCallback((period: string) => {
    setSelectedPeriod(period);
  }, []);

  const handleSubfilterChange = useCallback((subfilter: string) => {
    setSelectedSubfilter(subfilter);
  }, []);

  const handleTabChange = useCallback(
    (event: React.SyntheticEvent, newValue: number) => {
      if (activeFilter === 'Non-O&G vs O&G comparison') {
        setSelectedPeriod(timeperiods[0]);
        setSelectedSubfilter(subfilters[0]);
      }
      setActiveTab(newValue);
    },
    [activeFilter, timeperiods, subfilters],
  );

  const memoizedParams = useMemo(
    () => ({
      period: selectedPeriod,
      time: selectedSubfilter,
    }),
    [selectedPeriod, selectedSubfilter],
  );
  useEquipmentLevel(memoizedParams);

  useEffect(() => {
    setShowLoader(isLoading);
  }, [isLoading]);

  const tabs = useMemo(
    () => (
      <Tabs value={activeTab} onChange={handleTabChange} centered>
        <Tab label="About" />
        <Tab label="Explore Data" />
        <Tab label="Contact" />
      </Tabs>
    ),
    [activeTab, handleTabChange],
  );

  return (
    <div className="dashboard">
      {showLoader && (
        <div className="loader-overlay">
          <CircularProgress size={'5rem'} style={{ color: '#C15602' }} />
        </div>
      )}
      <Header
        logo={logo}
        filters={filters}
        subfilters={subfilters}
        operators={operators}
        timeperiods={
          activeFilter === 'Benchmarking' ? filteredTimeperiods : timeperiods
        }
        onFilterChange={handleFilterChange}
        info=""
        onPeriodChange={handlePeriodChange}
        onSubfilterChange={handleSubfilterChange}
        hideFilters={activeTab === 1}
        disableTimeperiods={
          // activeFilter === 'Facility-level (O&G)' ||
          // activeFilter === 'Non-O&G vs O&G comparison'
          false
        }
        disableOGType={activeFilter === 'Non-O&G vs O&G comparison'}
        enableOperators={activeFilter === 'Benchmarking'}
        tabsPortal={tabs}
      />
      <Box sx={{ padding: 2, paddingBottom: 0 }}>
        {activeTab === 0 && (
          <Typography variant="body1" sx={{ marginTop: '70px' }}>
            <h3>About the Model</h3>
            <p>
              The Appalachian Methane Initiative (AMI) employs a suite of
              advanced measurement technologies to complement and enhance the
              emissions information gathered from one of the largest oil and gas
              fields in the United States. These technologies are designed to
              detect, attribute, and quantify methane emissions with high
              precision and at various scales, from individual facilities to
              larger regional assessments.
            </p>
            <ul>
              <li>
                Bridger Photonics: Bridger Photonics gas mapping LIDAR uses a
                downward looking laser system that sweeps perpendicularly across
                the direction of flight of an aircraft and calculates a
                path-integrated methane concentration (ppm-m) from the aircraft
                to the ground. Bridger's technology is based on frequency
                modulated continuous-wave LIDAR. This system detects
                concentrated (point source) emissions that produce sufficient
                imaging contrast to separate the emissions plume from background
                methane concentrations.
              </li>
              <li>
                ChampionX: ChampionX uses a cavity ringdown spectrometer on an
                aerial platform to conduct mass balance measurements. A Picarro
                G2301 gas analyzer (Picarro Inc., Santa Clara, CA) measured
                ambient CH4 concentrations. They conducted aerial mass balance
                measurements that consists of perimeter box flights around the
                region of interest through the methane plume column. The
                measurement begins at as low an altitude as allowed by FAA
                regulations (e.g., 200 ft - 400 ft above ground level) and
                climbing until on-board measurements detect no plumes. This is
                indicated by the absence of any strong methane concentration
                enhancement upwind or downwind of the region.
                <br />
                <img
                  src="/assets/championx.png__545.0x278.0_subsampling-2.png"
                  alt=""
                  className="img-fluid"
                />
              </li>
              <li>
                Continuous Monitoring Systems (CMS): The AMI also incorporates a
                range of Continuous Monitoring Systems from various technology
                vendors. Most of these systems use a network of point-in-space
                sensors to measure methane concentrations in near real-time.
                These sensors are strategically placed to monitor emissions
                continuously, providing a dynamic picture of methane release.
                With the help of inversion algorithms, these systems can
                attribute and quantify emissions at the equipment level,
                offering detailed insights into the sources of methane
                emissions.
              </li>
            </ul>
            <p>
              Looking ahead to 2024 and beyond, the AMI plans to expand its
              measurement capabilities by potentially incorporating analysis of
              publicly available satellite information. Satellite data can offer
              a broader perspective and complement ground-based and aerial
              measurements, providing a more complete understanding of methane
              emissions over time and across larger geographic areas.
            </p>

            <h3>Further Reading</h3>
            <ul>
              <li>
                Ravikumar, A.P., Stokes, S., Wang, L., Sharafutdinov, E., Yang,
                S. (2023) Appalachian Methane Initiative 2023 - Multi-scale
                Methane Measurements in the Appalachian Shale Basin: A Pilot
                Study.
              </li>
              <li>
                Wang, J., Stokes, S. N., Sharafutdinov, E., Ravikumar, A. P.,
                Allen, D. (2023). Developing Facility-Level Measurement-Informed
                Inventory Estimates for Methane Emissions in the Oil and Gas
                Industry. AGU23.
              </li>
              <li>
                Daniels, W., Wang, J. L., Ravikumar, A., Harrison, M.,
                Roman-White, S., George, F., Hammerling, D. (2023). Towards
                multi-scale measurement-informed methane inventories:
                reconciling bottom-up site-level inventories with top-down
                measurements using continuous monitoring systems.
              </li>
            </ul>

            <h3>About EEMDL</h3>
            <p>
              The Energy Emissions Modeling and Data Lab (EEMDL) is a
              multi-disciplinary research and education center that operates as
              a global data and analytics hub, focusing on improving greenhouse
              gas emissions accounting across energy supply chains. This
              initiative is a collaborative effort involving the University of
              Texas at Austin, Colorado State University, and the Colorado
              School of Mines. EEMDL's mission is to provide reliable,
              science-based, transparent, and measurement-based greenhouse gas
              emissions assessments of global oil and gas supply chains. The lab
              aims to achieve this through the development of community models
              and tools for greenhouse gas emissions assessments, the provision
              of publicly available timely, high-resolution emissions datasets,
              and the creation of educational and training materials to enable
              widespread use of EEMDL's models and data.
            </p>
            <p>
              The lab is focused on addressing the challenge of interpreting
              measurements to develop supply chain emissions intensities or
              measurement-informed emissions inventories. To achieve this, EEMDL
              leverages recent advances in technologies such as aerial systems,
              satellites, and continuous emissions monitoring systems to gain
              unprecedented insight into global oil and gas methane emissions.
              By providing standardized ways to interpret greenhouse gas
              emissions measurement data, EEMDL aims to support improved
              greenhouse gas emissions accounting across energy supply chains.
            </p>
            <p>
              For questions, please contact Arvind Ravikumar at{' '}
              <a
                href="mailto:arvind.ravikumar@austin.utexas.edu"
                target="_blank"
                aria-description="Opens in new window."
              >
                arvind.ravikumar@austin.utexas.edu
              </a>
              .
            </p>
            <p>
              For inquiries on collaborative projects with EEMDL, please contact{' '}
              <a
                href="mailto:eemdl@utexas.edu"
                target="_blank"
                aria-description="Opens in new window."
              >
                eemdl@utexas.edu
              </a>
              .
            </p>

            <h3>About CEESA</h3>
            <p>
              CEESA provides data-driven, science-based research to inform
              global policy and support real-world emissions reductions across
              the energy sector. Our mission is to provide rigorous,
              measurement-informed insights that empower decision-makers to
              drive meaningful emissions reductions.
            </p>
          </Typography>
        )}
        {activeTab === 1 && (
          <div>
            {activeFilter === 'Equipment-level (O&G)' && (
              <EquipmentLevel
                period={selectedPeriod}
                time={selectedSubfilter}
              />
            )}
            {activeFilter === 'Facility-level (O&G)' && (
              <FacilityLevel period={selectedPeriod} time={selectedSubfilter} />
            )}
            {activeFilter === 'Non-O&G vs O&G comparison' && (
              <ComparisonLevel
                period={selectedPeriod}
                time={selectedSubfilter}
              />
            )}
            {activeFilter === 'Benchmarking' && (
              <BenchmarkLevel
                period={selectedPeriod}
                time={selectedSubfilter}
              />
            )}
          </div>
        )}
        {activeTab === 2 && (
          <Typography variant="body1" sx={{ marginTop: '70px' }}>
            <h3>Contact</h3>
            <p>
              The Appalachian Methane Initiative (AMI) is a collaborative effort
              involving a diverse team of stakeholders, including the Energy
              Emissions Modeling and Data Lab (EEMDL), SLR, and major natural
              gas companies such as EQT Corporation (EQT), Chesapeake Energy
              Corporation (CHK), Equitrans Midstream Corporation (ETRN), and
              others.
            </p>
            <p>
              For questions, please contact Arvind Ravikumar at{' '}
              <a
                href="mailto:arvind.ravikumar@austin.utexas.edu"
                target="_blank"
                aria-description="Opens in new window."
              >
                arvind.ravikumar@austin.utexas.edu
              </a>{' '}
              or Matt Harrison at{' '}
              <a
                href="mailto:mrharrison@slrconsulting.com"
                target="_blank"
                aria-description="Opens in new window."
              >
                mrharrison@slrconsulting.com
              </a>
              .
            </p>
            <p>
              For inquiries on collaborative projects with EEMDL, please contact{' '}
              <a
                href="mailto:eemdl@utexas.edu"
                target="_blank"
                aria-description="Opens in new window."
              >
                eemdl@utexas.edu
              </a>
              .
            </p>
            <br />
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <img
                  src="/assets/eemdl_logo.png__200x123_subsampling-2.png"
                  alt=""
                  width="200"
                  className="img-fluid"
                />
              </Grid>
              <Grid item xs={4}>
                <img
                  src="/assets/slr.jpg__200x123_q85_subsampling-2.jpg"
                  alt=""
                  width="200"
                  className="img-fluid"
                />
              </Grid>
              <Grid item xs={4}>
                <img
                  src="/assets/ascent-logo-colorb9b1ee2b8c43.png__200x123_subsampling-2.png"
                  alt=""
                  width="200"
                  className="img-fluid"
                />
              </Grid>
              <Grid item xs={4}>
                <img
                  src="/assets/eqt.png__200x123_subsampling-2.png"
                  alt=""
                  width="200"
                  className="img-fluid"
                />
              </Grid>
              <Grid item xs={4}>
                <img
                  src="/assets/chesapeake.png__200x123_subsampling-2.png"
                  alt=""
                  width="200"
                  className="img-fluid"
                />
              </Grid>
              <Grid item xs={4}>
                <img
                  src="/assets/equitrans.png__200x123_subsampling-2.png"
                  alt=""
                  width="200"
                  className="img-fluid"
                />
              </Grid>
              <Grid item xs={4}>
                <img
                  src="/assets/seneca.jpg__200x123_q85_subsampling-2.jpg"
                  alt=""
                  width="200"
                  className="img-fluid"
                />
              </Grid>
              <Grid item xs={4}>
                <img
                  src="/assets/mplx-color.png__200x123_subsampling-2.png"
                  alt=""
                  width="200"
                  className="img-fluid"
                />
              </Grid>
              <Grid item xs={4}>
                <img
                  src="/assets/cnx_resources_corporation_logo.jpg__200x123_q85_subsampling-2.jpg"
                  alt=""
                  width="200"
                  className="img-fluid"
                />
              </Grid>
            </Grid>
          </Typography>
        )}
      </Box>
    </div>
  );
});

export default Dashboard;
